import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Users,
  Building,
  Plug,
  // Tags,
  ChevronLeft,
  ChevronRight,
  Activity,
  LogOut,
  Shield,
  ScrollText,
  Settings,
  Video,
  Gauge,
  GitCompare,
  LucideIcon,
} from 'lucide-react';
import { getAppPublicPathUrl } from '../../../utils/tenant';
import { clearAllAuth } from '../../../components/auth/SubdomainAuthGuard.tsx';
import { ADMIN_PATHS } from '../../../routing/adminPaths.ts';
import { AUTH_PATHS } from '../../../routing/paths.ts';

interface SidebarProps {
  collapsed: boolean;
  onCollapseChange: (collapsed: boolean) => void;
}

interface SidebarMenuItem {
  path: string;
  label: string;
  icon: LucideIcon;
}

const MENU_ITEMS: SidebarMenuItem[] = [
    { path: ADMIN_PATHS.DASHBOARD, label: 'Dashboard', icon: LayoutDashboard },
    { path: ADMIN_PATHS.ORGANIZATIONS, label: 'Organizations', icon: Building },
    { path: ADMIN_PATHS.USERS, label: 'Users', icon: Users },
    { path: ADMIN_PATHS.ROLE_ACCESS, label: 'Role Access', icon: Shield },
    { path: ADMIN_PATHS.AUDIT_LOG, label: 'Audit Log', icon: ScrollText },
    { path: ADMIN_PATHS.SOP_VIDEOS, label: 'SOP Videos', icon: Video },
    { path: ADMIN_PATHS.AI_USAGE, label: 'AI Usage', icon: Gauge },
    { path: ADMIN_PATHS.MODEL_COMPARE, label: 'Model Compare', icon: GitCompare },
    { path: ADMIN_PATHS.INTEGRATIONS, label: 'Integrations', icon: Plug },
    { path: ADMIN_PATHS.PLATFORM_SETTINGS, label: 'Platform Settings', icon: Settings },
    // { path: ADMIN_PATHS.INTEGRATION_TYPES, label: 'Integration Types', icon: Tags },
];

const Sidebar: React.FC<SidebarProps> = ({ collapsed, onCollapseChange }) => {
  const location = useLocation();

  const isActive = (path: string): boolean => {
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  return (
    <div
      className={`${
        collapsed ? 'w-16' : 'w-56'
      } bg-blue-800 h-screen flex flex-col fixed left-0 top-0 transition-all duration-300 ease-in-out z-40`}
    >
      {/* Logo Section */}
      <div className="p-4">
        <div className={`flex items-center ${collapsed ? 'justify-center' : ''}`}>
          <div className="bg-white rounded-lg p-2 flex-shrink-0">
            <Activity className="h-6 w-6 text-blue-800" />
          </div>
          {!collapsed && (
            <div className="ml-3 transition-opacity duration-300">
              <h1 className="text-xl font-bold text-white" style={{
                fontFamily: "'Inter', 'SF Pro Display', -apple-system, sans-serif",
                letterSpacing: "0.1em",
                fontWeight: "700",
              }}>
                ADMIN
              </h1>
            </div>
          )}
        </div>
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4">
        {MENU_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.path);

          return (
            <Link
              key={item.path}
              to={item.path}
              title={collapsed ? item.label : ''}
              className={`flex items-center ${
                collapsed ? 'justify-center' : 'justify-between'
              } px-3 py-2 mb-1 text-sm font-medium rounded-lg transition-all ${
                active
                  ? 'bg-white text-blue-800'
                  : 'text-blue-100 hover:bg-blue-700 hover:text-white'
              }`}
            >
              <div className={`flex items-center ${collapsed ? 'justify-center' : ''}`}>
                <Icon className={`h-5 w-5 flex-shrink-0 ${!collapsed ? 'mr-3' : ''}`} />
                {!collapsed && <span>{item.label}</span>}
              </div>
            </Link>
          );
        })}
      </nav>

      {/* Logout */}
      <div className="border-t border-blue-700 px-4 py-3">
        <a
          href={getAppPublicPathUrl(AUTH_PATHS.SIGNIN)}
          onClick={(e) => {
            e.preventDefault();
            clearAllAuth();
            // Redirect immediately - full reload ensures token cleared, no dashboard flash
            window.location.replace(getAppPublicPathUrl(`${AUTH_PATHS.SIGNIN}?logout=1`));
          }}
          className="flex items-center w-full py-2 px-3 rounded-lg text-blue-200 hover:bg-blue-700 hover:text-white transition-all duration-300 group"
          title="Logout"
        >
          <LogOut className={`h-5 w-5 flex-shrink-0 ${!collapsed ? 'mr-3' : ''}`} />
          {!collapsed && <span>Logout</span>}
        </a>
      </div>

      {/* Collapse/Expand Button */}
      <div className="border-t border-blue-700 px-4 py-3">
        <button
          onClick={() => onCollapseChange(!collapsed)}
          className="flex items-center justify-center w-full py-2 rounded-lg hover:bg-blue-700 transition-all duration-300 group"
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ChevronRight className="h-5 w-5 text-blue-200 group-hover:text-white transition-colors" />
          ) : (
            <ChevronLeft className="h-5 w-5 text-blue-200 group-hover:text-white transition-colors" />
          )}
        </button>
      </div>
    </div>
  );
};

export default Sidebar;
